import { Link } from 'react-router-dom';
import { UserPlus, Ticket, ListChecks, Sparkles } from 'lucide-react';

/**
 * Post-conversion nudge card. Shown ONLY to anonymous callers immediately
 * after they complete a public RSVP or a ticket purchase — the moment they
 * feel the value of Unight most viscerally, which is when opt-in conversion
 * peaks. Never shown to signed-in users.
 *
 * Pre-fills the sign-up form via `?email=…&name=…&from=…` query params so
 * account creation is a single click for the guest we already have data on.
 *
 * Props:
 *   email    — required; the just-used contact email
 *   name     — optional; pre-fills the display name field on /signup
 *   context  — 'rsvp' | 'ticket'; toggles copy so the card doesn't look
 *              generic. Both variants share the same value proposition.
 */
export default function SignupNudgeCard({ email, name, context = 'rsvp' }) {
  if (!email) return null;

  const query = new URLSearchParams({
    email,
    ...(name ? { name } : {}),
    from: context,
  }).toString();

  const headline =
    context === 'ticket'
      ? 'MAKE THIS TICKET YOURS FOREVER'
      : 'KEEP THIS RSVP IN YOUR POCKET';

  const subhead =
    context === 'ticket'
      ? 'Create a free account so this ticket lives in your dashboard alongside every future purchase — no more inbox archaeology.'
      : "Create a free account and this RSVP joins every future one in your dashboard. Cancel, forward, or re-download a QR anytime.";

  return (
    <div
      className="border border-[#B5FF00]/40 bg-gradient-to-br from-[#B5FF00]/[0.08] to-[#B5FF00]/[0.02] p-6 mt-6 text-left"
      data-testid={`signup-nudge-${context}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 shrink-0 rounded-full bg-[#B5FF00]/15 flex items-center justify-center">
          <Sparkles size={18} className="text-[#B5FF00]" />
        </div>
        <div>
          <h3 className="font-['Josefin_Sans'] text-lg sm:text-xl text-white leading-tight">
            {headline}
          </h3>
          <p className="text-zinc-400 text-sm mt-1">{subhead}</p>
        </div>
      </div>

      <ul className="text-zinc-300 text-sm space-y-2 mb-5">
        <li className="flex items-center gap-2">
          <ListChecks size={14} className="text-[#B5FF00] shrink-0" />
          One dashboard for every RSVP + ticket
        </li>
        <li className="flex items-center gap-2">
          <Ticket size={14} className="text-[#B5FF00] shrink-0" />
          Re-download QRs anytime, no email digging
        </li>
        <li className="flex items-center gap-2">
          <UserPlus size={14} className="text-[#B5FF00] shrink-0" />
          Skip filling in your details next time
        </li>
      </ul>

      <Link
        to={`/signup?${query}`}
        className="btn-primary w-full flex items-center justify-center gap-2 text-center"
        data-testid={`signup-nudge-cta-${context}`}
      >
        <UserPlus size={16} />
        CREATE FREE ACCOUNT
      </Link>
      <p className="font-mono text-[10px] text-zinc-500 mt-3 text-center tracking-wider">
        Takes 20 seconds · we&rsquo;ll pre-fill your email
      </p>
    </div>
  );
}
